import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Checkbox, Form, Input, Result, Select } from "antd";
import axios from "axios";
import { publicFetchJson, publicPostJson } from "../utils/apiHelper";
import {
  getPublicFormConfigUrl,
  getPublicFormTokenUrl,
} from "../redux/actions/formsAction";
import { validateClientFormFields } from "../pages/App/Forms/formUtils";

const { TextArea } = Input;

function loadRecaptchaScript(siteKey) {
  return new Promise((resolve, reject) => {
    if (!siteKey) {
      resolve();
      return;
    }
    if (window.grecaptcha) {
      resolve();
      return;
    }
    const existing = document.getElementById("mailinflow-recaptcha");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.id = "mailinflow-recaptcha";
    script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

function EmbeddedField({ field }) {
  const rules = field.required
    ? [{ required: true, message: `${field.label} is required` }]
    : [];

  if (field.type === "textarea") {
    return (
      <Form.Item name={field.name} label={field.label} rules={rules}>
        <TextArea rows={3} placeholder={field.placeholder} />
      </Form.Item>
    );
  }

  if (field.type === "select") {
    return (
      <Form.Item name={field.name} label={field.label} rules={rules}>
        <Select
          placeholder={field.placeholder || `Select ${field.label}`}
          options={(field.options || []).map((opt) => ({
            value: opt.value,
            label: opt.label,
          }))}
          allowClear
        />
      </Form.Item>
    );
  }

  if (field.type === "checkbox") {
    return (
      <Form.Item
        name={field.name}
        valuePropName="checked"
        rules={
          field.required
            ? [
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(`${field.label} must be checked`),
                        ),
                },
              ]
            : []
        }
      >
        <Checkbox>{field.label}</Checkbox>
      </Form.Item>
    );
  }

  const inputType =
    field.type === "email" ? "email" : field.type === "phone" ? "tel" : "text";

  return (
    <Form.Item name={field.name} label={field.label} rules={rules}>
      <Input type={inputType} placeholder={field.placeholder} />
    </Form.Item>
  );
}

/**
 * Compact, inline version of the hosted form page (src/pages/Public/HostedForm.js)
 * for embedding a real Mailinflow form directly in marketing pages — the
 * product's own lead-gen forms, used on its own site. `formId` is the
 * form's publicId; if not configured yet, renders nothing rather than a
 * broken form.
 */
export default function EmbeddedLeadForm({
  formId,
  submitLabel = "Submit",
  successTitle = "Thanks!",
  successSubtitle = "We'll be in touch soon.",
}) {
  const [form] = Form.useForm();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(Boolean(formId));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [configError, setConfigError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [antiSpamToken, setAntiSpamToken] = useState("");
  const recaptchaRef = useRef(null);
  const recaptchaWidgetId = useRef(null);
  // The honeypot is a plain hidden input, not an antd Form.Item, so its
  // value never reached onFinish(values) and the API's honeypot check
  // (POST /f/:publicId, settings.honeypotField) had nothing to look at. It
  // is read through this ref and sent with the submission.
  const honeypotRef = useRef(null);
  const honeypotField = config?.honeypotField || "_gotcha";

  const loadToken = useCallback(() => {
    if (!formId) return Promise.resolve();
    return axios
      .get(getPublicFormTokenUrl(formId), {
        headers: { Accept: "application/json" },
      })
      .then((res) => {
        if (res.data?.token) setAntiSpamToken(res.data.token);
      })
      .catch(() => {});
  }, [formId]);

  useEffect(() => {
    if (!formId) return undefined;
    let cancelled = false;
    setLoading(true);
    setConfigError("");

    publicFetchJson(getPublicFormConfigUrl(formId))
      .then(async (res) => {
        if (cancelled) return;
        const data = res.data;
        setConfig(data);
        await loadToken();
        if (data.recaptcha?.enabled && data.recaptcha.siteKey) {
          await loadRecaptchaScript(data.recaptcha.siteKey);
          if (!cancelled && window.grecaptcha && recaptchaRef.current) {
            recaptchaWidgetId.current = window.grecaptcha.render(
              recaptchaRef.current,
              { sitekey: data.recaptcha.siteKey },
            );
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err.response?.data?.error ||
          (err.response?.status
            ? `Failed to load form (HTTP ${err.response.status}).`
            : "Failed to load form.");
        console.error(`EmbeddedLeadForm(${formId}):`, message, err);
        setConfigError(message);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [formId, loadToken]);

  if (!formId) return null;
  if (loading) return null;
  if (configError) {
    return (
      <Alert
        type="error"
        showIcon
        message="This form could not be loaded"
        description={configError}
      />
    );
  }
  if (!config) return null;

  if (submitted) {
    return <Result status="success" title={successTitle} subTitle={successSubtitle} />;
  }

  const onFinish = async (values) => {
    setSubmitting(true);
    setError("");

    const clientCheck = validateClientFormFields(
      config.fields,
      config.validation,
      values,
    );
    if (!clientCheck.ok) {
      setError(clientCheck.errors[0]?.message || "Please check the form.");
      setSubmitting(false);
      return;
    }

    const payload = { ...values, antiSpamToken };
    const honeypotValue = honeypotRef.current?.value || "";
    if (honeypotValue) payload[honeypotField] = honeypotValue;
    if (config.recaptcha?.enabled) {
      const token = window.grecaptcha?.getResponse(recaptchaWidgetId.current);
      if (!token) {
        setError("Please complete the captcha.");
        setSubmitting(false);
        return;
      }
      payload["g-recaptcha-response"] = token;
    }

    try {
      const res = await publicPostJson(`/f/${formId}`, payload);
      if (res.data?.ok) {
        setSubmitted(true);
        return;
      }
      setError(res.data?.error || "Submission failed.");
    } catch (err) {
      const data = err.response?.data;
      setError(
        data?.error ||
          data?.errors?.[0]?.message ||
          "Something went wrong. Please try again.",
      );
      if (window.grecaptcha && recaptchaWidgetId.current != null) {
        window.grecaptcha.reset(recaptchaWidgetId.current);
      }
      await loadToken();
    } finally {
      setSubmitting(false);
    }
  };

  const sortedFields = [...(config.fields || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );

  return (
    <div>
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Form form={form} layout="vertical" onFinish={onFinish}>
        {sortedFields.map((field) => (
          <EmbeddedField key={field.id || field.name} field={field} />
        ))}

        <input
          ref={honeypotRef}
          type="text"
          name={honeypotField}
          style={{ display: "none" }}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />

        {config.recaptcha?.enabled && config.recaptcha.siteKey && (
          <Form.Item>
            <div ref={recaptchaRef} />
          </Form.Item>
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={submitting}
          >
            {submitLabel}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
